import React, { useEffect, useState } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { recommendationsService, RecommendationsApiError } from '../services/recommendations.service';
import { CompanyRecommendation } from '../types';

interface RecommendationsSectionProps {
  companyId: number;
  companyName: string;
  limit?: number;
  onCompanyClick?: (companyId: number) => void;
}

// ---- shared shell with section header ----

const SectionShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: COLORS.user.queryButtonLight }}
      >
        <svg
          className="w-5 h-5"
          style={{ color: COLORS.user.queryButton }}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
        </svg>
      </div>
      <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
        Similar Companies
      </h3>
    </div>
    {children}
  </div>
);

// ---- explicit state sub-components ----

const LoadingState: React.FC = () => (
  <div className="bg-white rounded-2xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: COLORS.user.queryButtonLight }}
      >
        <svg
          className="w-5 h-5"
          style={{ color: COLORS.user.queryButton }}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
        </svg>
      </div>
      <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
        Similar Companies
      </h3>
    </div>
    <div className="flex flex-col items-center justify-center py-8 gap-3">
      <div
        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
        style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
      />
      <p className="text-sm text-gray-500">Analyzing similar companies…</p>
    </div>
  </div>
);

const PremiumState: React.FC = () => (
  <SectionShell>
    <div className="flex flex-col items-center text-center py-6 gap-4">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: COLORS.user.queryButtonLight }}
      >
        <svg
          className="w-7 h-7"
          style={{ color: COLORS.user.queryButton }}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800 mb-1">
          Similar Companies is a premium feature
        </p>
        <p className="text-sm text-gray-500 max-w-xs">
          Upgrade to unlock AI-matched companies from across your portfolio.
        </p>
      </div>
      <a
        href="/billing"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90"
        style={{ backgroundColor: COLORS.user.primary }}
      >
        Upgrade to unlock
      </a>
    </div>
  </SectionShell>
);

interface ErrorStateProps {
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ onRetry }) => (
  <SectionShell>
    <div className="flex flex-col items-center text-center py-6 gap-4">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-red-50">
        <svg className="w-7 h-7 text-red-400" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800 mb-1">
          Couldn't load similar companies
        </p>
        <p className="text-sm text-gray-500">Please try again.</p>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors hover:bg-gray-50"
        style={{ borderColor: COLORS.user.primary, color: COLORS.user.primary }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Retry
      </button>
    </div>
  </SectionShell>
);

const EmptyState: React.FC<{ companyName: string }> = ({ companyName }) => (
  <SectionShell>
    <div className="flex flex-col items-center text-center py-6 gap-3">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gray-100">
        <svg className="w-7 h-7 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-1">
          No similar companies found yet
        </p>
        <p className="text-sm text-gray-400 max-w-xs">
          Similar companies for {companyName} may appear after enrichment data is processed.
        </p>
      </div>
    </div>
  </SectionShell>
);

// ---- main component ----

export const RecommendationsSection: React.FC<RecommendationsSectionProps> = ({
  companyId,
  companyName,
  limit = 5,
  onCompanyClick,
}) => {
  const [recommendations, setRecommendations] = useState<CompanyRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * null  = no error (initial or success)
   * 403   = premium required
   * 0     = network-level / unknown error
   * other = HTTP status (e.g. 500)
   */
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadRecommendations = async () => {
    setIsLoading(true);
    setErrorStatus(null);
    setRecommendations([]);
    try {
      const data = await recommendationsService.getCompanyRecommendations(
        companyId,
        limit,
        true // generate insights
      );
      setRecommendations(data.recommendations);
    } catch (err: any) {
      if (err instanceof RecommendationsApiError) {
        setErrorStatus(err.status);
      } else {
        // Network-level or unknown error
        setErrorStatus(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return { bg: '#D1FAE5', text: '#059669' };
    if (score >= 0.6) return { bg: '#E3F2FD', text: '#1565C0' };
    if (score >= 0.4) return { bg: '#FFF3E0', text: '#E65100' };
    return { bg: '#F3F4F6', text: '#6B7280' };
  };

  // 1. Loading — shown while the (potentially slow) similarity computation runs
  if (isLoading) {
    return <LoadingState />;
  }

  // 2. Premium required (403)
  if (errorStatus === 403) {
    return <PremiumState />;
  }

  // 3. Other error (500 / network / timeout)
  if (errorStatus !== null) {
    return <ErrorState onRetry={loadRecommendations} />;
  }

  // 4. Empty — 200 OK but no results
  if (recommendations.length === 0) {
    return <EmptyState companyName={companyName} />;
  }

  // 5. Happy path — recommendation cards
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.user.queryButtonLight }}
          >
            <svg
              className="w-5 h-5"
              style={{ color: COLORS.user.queryButton }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
              Similar Companies
            </h3>
            <p className="text-xs text-gray-500">
              Companies similar to {companyName}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs font-medium hover:underline"
          style={{ color: COLORS.user.primary }}
        >
          {isExpanded ? 'Show less' : `View all ${recommendations.length}`}
        </button>
      </div>

      <div className="space-y-3">
        {recommendations.slice(0, isExpanded ? recommendations.length : 3).map((rec) => {
          const scoreColors = getScoreColor(rec.combined_score);
          return (
            <div
              key={rec.company_id}
              onClick={() => onCompanyClick?.(rec.company_id)}
              className="p-4 rounded-lg border hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              style={{ borderColor: '#E5E7EB' }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-sm" style={{ color: '#1A1A1A' }}>
                      {rec.company_name}
                    </h4>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: scoreColors.bg, color: scoreColors.text }}
                    >
                      {(rec.combined_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  {rec.description && (
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                      {rec.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {rec.industry && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                        </svg>
                        {rec.industry}
                      </span>
                    )}
                    {rec.location && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {rec.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {rec.insights && (
                <div className="mt-2 pt-2 border-t" style={{ borderColor: '#F0F0F0' }}>
                  <p className="text-xs text-gray-600 italic">
                    {rec.insights.partnership_rationale}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
